import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from 'store/store';
import { ACCESS_BASE_URL } from 'App';
import {
  useGetAccountTransactions,
  useGetUiAccountsBalances,
  PageToken,
  useGetSupportedDigitalAssets,
  useGetSupportedBlockchains,
  usePostAccountsTransfers,
  getDepositAddresses
} from '@utx/utx-access-client';

export interface useAccountBalanceParams {
  accountId: string;
  asset?: string;
}

export const useAccountBalance = ({ accountId, asset }: useAccountBalanceParams) => {
  const store = useStore();
  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken ? true : false);
  }, 100);

  const queryAccountBalances = useGetUiAccountsBalances(
    {
      accountId: [accountId],
      asset: asset
    },
    {
      axios: {
        baseURL: ACCESS_BASE_URL
      },
      query: {
        queryKey: ['queryAccountBalances', store.authToken, accountId, asset],
        retry: false,
        enabled,
        keepPreviousData: true
      }
    }
  );

  const accountsTransfersMutate = usePostAccountsTransfers({ axios: { baseURL: ACCESS_BASE_URL } });
  const accountsTransfers = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    accountsTransfersMutate.mutate(
      {
        data
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const balances = useMemo(() => {
    const data = queryAccountBalances.data?.data.balances;
    return data || [];
  }, [queryAccountBalances.data?.data]);

  const isLoadingAccountBalances = useMemo(() => {
    return queryAccountBalances.isLoading;
  }, [queryAccountBalances.isLoading]);

  return {
    balances,
    isLoadingAccountBalances,
    reloadAccountBalances: () => queryAccountBalances.refetch(),
    accountsTransfers
  };
};

export interface useAccountTransactionParams {
  accountId: string;
  pageSize?: number;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  search?: string;
}

export const useAccountTransaction = ({ accountId, pageSize = 10, dateRangeFrom, dateRangeTo, search }: useAccountTransactionParams) => {
  const store = useStore();

  const [currentPageToken, setCurrentPageToken] = useState<PageToken>();
  const [pageToken, setPageToken] = useState<{ nextToken?: PageToken; preToken?: PageToken }>({});
  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken ? true : false);
  }, 100);

  const queryAccountTransactions = useGetAccountTransactions(
    {
      accountId: [accountId],
      pageSize: pageSize,
      pageToken: currentPageToken,
      dateRangeFrom: dateRangeFrom,
      dateRangeTo: dateRangeTo,
      search: search
    },
    {
      axios: {
        baseURL: ACCESS_BASE_URL
      },
      query: {
        queryKey: ['queryAccountTransactions', store.authToken, accountId, pageSize, currentPageToken, dateRangeFrom, dateRangeTo, search],
        retry: false,
        enabled,
        keepPreviousData: true
      }
    }
  );

  const transactions = useMemo(() => {
    const data = queryAccountTransactions.data?.data.transactions;
    return data || [];
  }, [queryAccountTransactions.data?.data]);

  const isLoadingAccountTransactions = useMemo(() => {
    return queryAccountTransactions.isLoading;
  }, [queryAccountTransactions.isLoading]);

  // useEffect(() => {
  //   queryAccountTransactions.refetch();
  // }, [accountId, pageSize, pageToken, dateRangeFrom, dateRangeTo, search]);

  useEffect(() => {
    setPageToken({
      nextToken: queryAccountTransactions.data?.data.nextPageToken,
      preToken: queryAccountTransactions.data?.data.prevPageToken
    });
  }, [queryAccountTransactions.data?.data]);

  return {
    transactions,
    isLoadingAccountTransactions,
    reloadAccountTransactions: () => queryAccountTransactions.refetch(),
    nextToken: pageToken.nextToken,
    preToken: pageToken.preToken,
    prev: () => setCurrentPageToken(pageToken.preToken),
    next: () => setCurrentPageToken(pageToken.nextToken),
    refreshAccountTransactions: () => {
      setCurrentPageToken(undefined);
      queryAccountTransactions.refetch();
    },
    clearCurrentPageToken: () => {
      setCurrentPageToken(undefined);
    }
  };
};

export interface useDigitalAssetsParams {}

export const useDigitalAssets = ({}: useDigitalAssetsParams) => {
  const store = useStore();
  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken && store.users?.metadata.earnAccount?.enabled);
  }, 100);

  const queryDigitalAssets = useGetSupportedDigitalAssets({
    axios: {
      baseURL: ACCESS_BASE_URL
    },
    query: {
      queryKey: ['queryDigitalAssets', store.authToken],
      retry: false,
      enabled,
      keepPreviousData: true
    }
  });

  const assets = useMemo(() => {
    const data = queryDigitalAssets.data?.data;
    return data || [];
  }, [queryDigitalAssets.data?.data]);

  const isLoadingDigitalAssets = useMemo(() => {
    return queryDigitalAssets.isLoading;
  }, [queryDigitalAssets.isLoading]);

  const queryBlockchains = useGetSupportedBlockchains({
    axios: {
      baseURL: ACCESS_BASE_URL
    },
    query: {
      queryKey: ['queryBlockchains', store.authToken],
      retry: false,
      enabled,
      keepPreviousData: true
    }
  });

  const chains = useMemo(() => {
    const data = queryBlockchains.data?.data;
    return data || [];
  }, [queryBlockchains.data?.data]);

  const isLoadingBlockchains = useMemo(() => {
    return queryBlockchains.isLoading;
  }, [queryBlockchains.isLoading]);

  return {
    assets,
    isLoadingDigitalAssets,
    reloadDigitalAssets: () => queryDigitalAssets.refetch(),
    chains,
    isLoadingBlockchains,
    reloadBlockchains: () => queryBlockchains.refetch()
  };
};

export interface getDepositAddressesParams {
  asset?: string;
  chain?: string;
  accountId?: string[];
}

export const useDepositAddresses = () => {
  const queryDepositAddresses = ({ asset, chain, accountId }: getDepositAddressesParams) => {
    return getDepositAddresses(
      {
        asset: asset,
        chain: chain,
        accountId: accountId
      },
      {
        baseURL: ACCESS_BASE_URL
      }
    );
  };

  return {
    queryDepositAddresses
  };
};
